import showdown from "showdown";
import DOMPurify from "dompurify";

/**
 *
 * @param {String} html
 * @returns HTML required for markdown conversion
 */
const getCleanJoditHTML = (html) => {
    if (!html) return;
    const strongBrRegex = /<strong(\s*[^>]*)?>\s*<br\s*\/?>\s*<\/strong>/gi;
    // [info]: do not actually want a strong tag with just br in it
    html = html.replace(strongBrRegex, "<br/>");

    /**
     * [info]: this showdown library only offers markdown conversion that are general and do allow any option for custom tags build in.
     *  so for these tags we have to insert our own custom tags
     * - highlight
     * - underline
     * - strikethrough
     *
     * also for some reason if we try to put actual html tags instead of these custom ones the library autmatically adds 2 line breaks after that.
     * https://github.com/showdownjs/showdown/blob/95255984ad80acf745ed74605bd3ad8357dc9b33/src/subParsers/makemarkdown/node.js#L121
     *
     * not sure if this is a default behaviour or a bug. so to put some custom tags in this format [tag_name] seems to be the best option.
     * */
    html = html
        .replace(/<span style="background-color: rgb\(255, 255, 0\);">/g, "[highlighted]")
        .replace(/<\/span>/g, "[/highlighted]");
    html = html.replace(/<u>/g, "[underline]").replace(/<\/u>/g, "[/underline]");
    html = html.replace(/<s>/g, "[strikethrough]").replace(/<\/s>/g, "[/strikethrough]");

    /**
     * [info]: for some reason showdown is not processing the space after the bold text. so we have to again use our custom tags for strong and italic ((
     * https://github.com/showdownjs/showdown/issues/907
     */
    html = html.replace(/<strong>/g, "[strong]").replace(/<\/strong>/g, "[/strong]");
    html = html.replace(/<em>/g, "[italic]").replace(/<\/em>/g, "[/italic]");

    // [info]: replace nbsp with space. showdown kind of remove nbsp automatically
    html = html.replace(/&nbsp;/g, " ");

    return html;
};

/**
 *
 * @param {String} html
 * @returns HTML which is secure and can prevent XSS
 */
const getSanitizeHTML = (html) => {
    return DOMPurify.sanitize(html);
};

/**
 *
 * @param {String} html
 * @returns Markdown to save in database
 */
const convertHTMLIntoMarkdown = (html) => {
    if (!html) return;
    const cleanHTML = getCleanJoditHTML(html);
    const sanitizeHTML = getSanitizeHTML(cleanHTML);
    const convertor = new showdown.Converter();
    return convertor.makeMarkdown(sanitizeHTML);
};

/**
 *
 * @param {String} markdown
 * @returns a sanitize HTML
 */
const convertMarkdownIntoHTML = (markdown) => {
    if (!markdown) return;
    const converter = new showdown.Converter();
    let html = converter.makeHtml(markdown);
    // [info]: here we are converting our custom tags to an actual html.
    html = html
        .replace(/\[highlighted\]/g, '<span style=\"background-color: rgb(255, 255, 0);\">')
        .replace(/\[\/highlighted\]/g, "</span>");
    html = html.replace(/\[underline\]/g, "<u>").replace(/\[\/underline\]/g, "</u>");
    html = html.replace(/\[strikethrough\]/g, "<s>").replace(/\[\/strikethrough\]/g, "</s>");
    html = html.replace(/\[strong\]/g, "<strong>").replace(/\[\/strong\]/g, "</strong>");
    html = html.replace(/\[italic\]/g, "<em>").replace(/\[\/italic\]/g, "</em>");
    return getSanitizeHTML(html);
};

/**
 *
 * @param {String} html
 * @returns plain text
 */
const getPlainTextFromHTML = (html) => {
    if (!html) return;
    const sanitizeHTML = getSanitizeHTML(html);
    const temporaryElement = document.createElement("div");
    temporaryElement.innerHTML = sanitizeHTML;
    return temporaryElement.textContent || temporaryElement.innerText || "";
};

/**
 *
 * @param {String} markdown
 * @returns plain text
 */
const getPlainTextFromMarkdown = (markdown) => {
    if (!markdown) return;
    const html = convertMarkdownIntoHTML(markdown);
    return getPlainTextFromHTML(html);
};

/**
 * Converts a markdown string to a format understood by WhatsApp.
 *
 * @param {String} markdown The markdown string to convert.
 * @returns {String} The WhatsApp formatted string.
 */

function convertMarkdownInWhatsappFormat(markdown) {
    if (!markdown) return;
    let formattedMarkdown = markdown;

    // [info]: removing the comments that were added by showdown
    formattedMarkdown = formattedMarkdown.replace(/<!.*?>/gs, "");

    // [info]: deleting the entire tag if its empty
    const conditionalWrapper = (wrapper) => (match, leadingSpace, content, trailingSpace) => {
        if (content.trim() === "") {
            return "";
        }
        return `${leadingSpace}${wrapper}${content}${wrapper}${trailingSpace}`;
    };

    // [info]: making sure there is not space before and after these tags * and -
    formattedMarkdown = formattedMarkdown.replace(/\[strong\](\s*)(.*?)(\s*)\[\/strong\]/gs, conditionalWrapper("*"));
    formattedMarkdown = formattedMarkdown.replace(/\[italic\](\s*)(.*?)(\s*)\[\/italic\]/gs, conditionalWrapper("_"));

    // [info]: making sure that there is always a space after > or -
    formattedMarkdown = formattedMarkdown.replace(/^> (.*)$/gm, "> $1");
    formattedMarkdown = formattedMarkdown.replace(/^- (.*)$/gm, "• $1");

    //[info]: cleaning up the line breaks issue created by jodit.
    formattedMarkdown = formattedMarkdown.replace(/(\n\s*){2,}/g, "\n");
    formattedMarkdown = formattedMarkdown.replace(/<br>/g, "");
    formattedMarkdown = formattedMarkdown.replace(
        /\[strikethrough\](\s*)(.*?)(\s*)\[\/strikethrough\]/gs,
        conditionalWrapper("~"),
    );
    return formattedMarkdown.trim();
}

export {
    convertHTMLIntoMarkdown,
    getSanitizeHTML,
    getCleanJoditHTML,
    convertMarkdownIntoHTML,
    getPlainTextFromMarkdown,
    convertMarkdownInWhatsappFormat,
};
